/**
 * ListAuditPage
 * 
 * Lista todas as auditorias de estoque em formato de tabela
 * Similar à página de incoming
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { 
  FileCheck, AlertTriangle, CheckCircle2, Clock, 
  Package, Search, ArrowRight, Loader2, X
} from 'lucide-react'
import { stockAuditService } from '../../../../services/erpApi'
import './ListAuditPage.css'

interface StockAudit {
  id: number
  audit_number: string
  brand_id: number
  brand_name: string
  status: 'pending' | 'in_progress' | 'completed' | 'applied' | 'cancelled'
  start_date: string
  end_date?: string
  created_by: number
  created_by_name?: string
  responsible_user_name?: string
  audited_by?: number
  audited_by_name?: string
  applied_by?: number
  applied_by_name?: string
  total_items?: number
  divergent_items?: number
  items_with_discrepancy?: number
  created_at: string
}

function ListAuditPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [audits, setAudits] = useState<StockAudit[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const loadAudits = useCallback(async () => {
    try {
      setLoading(true)
      const page = parseInt(searchParams.get('page') || '1')
      const status = searchParams.get('status') || statusFilter
      const search = searchParams.get('search') || searchTerm

      const filters: any = {
        page,
        limit: pagination.limit
      }

      if (status) {
        filters.status = status
      }
      if (search) {
        filters.search = search
      }

      const response = await stockAuditService.getList(filters)
      if (response.success) {
        // Mapear campos do backend para o frontend
        const mappedAudits = Array.isArray(response.data) ? response.data.map((audit: any) => ({
          ...audit,
          audit_number: audit.code || audit.audit_number,
          brand_name: audit.brand_name_full || audit.brand_name,
          divergent_items: audit.items_with_discrepancy || audit.divergent_items || 0
        })) : []
        setAudits(mappedAudits)
        if (response.pagination) {
          setPagination({
            page: response.pagination.page || page,
            limit: response.pagination.limit || pagination.limit,
            total: response.pagination.total || 0,
            totalPages: response.pagination.totalPages || 0
          })
        }
      }
    } catch (error) {
      console.error('Erro ao carregar auditorias:', error)
    } finally {
      setLoading(false)
    }
  }, [searchParams, statusFilter, searchTerm, pagination.limit])

  useEffect(() => {
    const statusParam = searchParams.get('status')
    const searchParam = searchParams.get('search')
    if (statusParam) setStatusFilter(statusParam)
    if (searchParam) setSearchTerm(searchParam)
  }, [searchParams])

  useEffect(() => {
    loadAudits()
  }, [loadAudits])

  const getStatusConfig = (audit: StockAudit) => {
    switch (audit.status) {
      case 'pending':
        return { label: 'Pendente', color: 'status-pending', icon: Clock }
      case 'in_progress':
        return { label: 'Em Progresso', color: 'status-in-progress', icon: Package }
      case 'completed':
        return { label: 'Concluída', color: 'status-completed', icon: CheckCircle2 }
      case 'applied':
        return { label: 'Aplicada', color: 'status-applied', icon: FileCheck }
      case 'cancelled':
        return { label: 'Cancelada', color: 'status-cancelled', icon: X }
      default:
        return { label: 'Desconhecido', color: 'status-default', icon: FileCheck }
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleRowClick = (audit: StockAudit) => {
    // Se a auditoria está concluída ou aplicada, ir para página de gerenciamento
    // Caso contrário, ir para página de contagem física
    if (audit.status === 'completed' || audit.status === 'applied') {
      navigate(`/erp/stock/audits/${audit.id}/manage`)
    } else {
      navigate(`/erp/stock/audits/${audit.id}`)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (statusFilter) params.set('status', statusFilter)
    navigate(`/erp/stock/audits/list?${params.toString()}`)
  }

  const filteredAudits = audits.filter(audit => {
    if (statusFilter && audit.status !== statusFilter) return false
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        audit.audit_number?.toLowerCase().includes(search) ||
        audit.brand_name?.toLowerCase().includes(search) ||
        audit.created_by_name?.toLowerCase().includes(search)
      )
    }
    return true
  })

  return (
    <div className="list-audit-page">
      {/* Header */}
      <div className="list-audit-header">
        <div>
          <h1 className="list-audit-title">
            Lista de <span className="list-audit-title-accent">Auditorias</span>
          </h1>
          <p className="list-audit-subtitle">
            Visualize e gerencie todas as auditorias de estoque
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="list-audit-filters">
        <form onSubmit={handleSearch} className="list-audit-search-form">
          <div className="list-audit-search">
            <Search className="list-audit-search-icon" size={14} />
            <input
              type="text"
              placeholder="Buscar por número, marca ou criador..."
              className="list-audit-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="list-audit-status-filter"
            value={statusFilter || ''}
            onChange={(e) => {
              setStatusFilter(e.target.value || null)
              const params = new URLSearchParams()
              if (e.target.value) params.set('status', e.target.value)
              if (searchTerm) params.set('search', searchTerm)
              navigate(`/erp/stock/audits/list?${params.toString()}`)
            }}
          >
            <option value="">Todos os Status</option>
            <option value="pending">Pendente</option>
            <option value="in_progress">Em Progresso</option>
            <option value="completed">Concluída</option>
            <option value="applied">Aplicada</option>
            <option value="cancelled">Cancelada</option>
          </select>
          <button type="submit" className="list-audit-search-btn">
            Buscar
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="list-audit-card">
        <div className="list-audit-card-header">
          <h3 className="list-audit-card-title">
            Auditorias ({pagination.total || filteredAudits.length})
          </h3>
        </div>

        {loading ? (
          <div className="list-audit-loading">
            <Loader2 size={24} className="list-audit-spinner" />
            <p>Carregando auditorias...</p>
          </div>
        ) : (
          <div className="list-audit-table-wrapper">
            <table className="list-audit-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Marca</th>
                  <th className="text-center">Itens</th>
                  <th className="text-center">Divergências</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Criado por</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredAudits.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="list-audit-empty">
                      <p>Nenhuma auditoria encontrada</p>
                    </td>
                  </tr>
                ) : (
                  filteredAudits.map((audit) => {
                    const config = getStatusConfig(audit)
                    const Icon = config.icon
                    return (
                      <tr
                        key={audit.id}
                        className="list-audit-table-row"
                        onClick={() => handleRowClick(audit)}
                      >
                        <td>
                          <div className="list-audit-audit-info">
                            <span className="list-audit-audit-number">
                              {audit.audit_number}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="list-audit-brand-name">
                            {audit.brand_name || `Marca ID ${audit.brand_id}`}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="list-audit-items-badge">
                            {audit.total_items || 0}
                          </span>
                        </td>
                        <td className="text-center">
                          {(audit.divergent_items || 0) > 0 ? (
                            <span className="list-audit-divergent-badge">
                              <AlertTriangle size={12} />
                              {audit.divergent_items}
                            </span>
                          ) : (
                            <span className="list-audit-no-divergent">-</span>
                          )}
                        </td>
                        <td>
                          <div className="list-audit-status-wrapper">
                            <span className={`list-audit-status ${config.color}`}>
                              <Icon size={12} />
                              {config.label}
                            </span>
                          </div>
                        </td>
                        <td>
                          <p className="list-audit-date">
                            <Clock size={10} /> {formatDate(audit.created_at)}
                          </p>
                        </td>
                        <td>
                          <span className="list-audit-created-by">
                            {audit.responsible_user_name || audit.created_by_name || `Usuário #${audit.created_by}`}
                          </span>
                        </td>
                        <td className="text-right">
                          <button 
                            className="list-audit-btn-action"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRowClick(audit)
                            }}
                          >
                            {(audit.status === 'completed' || audit.status === 'applied') ? 'Gerenciar' : 'Acessar'} <ArrowRight size={14} />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="list-audit-pagination">
            <button
              className="list-audit-pagination-btn"
              onClick={() => {
                const params = new URLSearchParams(searchParams)
                params.set('page', String(Math.max(1, pagination.page - 1)))
                navigate(`/erp/stock/audits/list?${params.toString()}`)
              }}
              disabled={pagination.page === 1}
            >
              Anterior
            </button>
            <span className="list-audit-pagination-info">
              Página {pagination.page} de {pagination.totalPages}
            </span>
            <button
              className="list-audit-pagination-btn"
              onClick={() => {
                const params = new URLSearchParams(searchParams)
                params.set('page', String(Math.min(pagination.totalPages, pagination.page + 1)))
                navigate(`/erp/stock/audits/list?${params.toString()}`)
              }}
              disabled={pagination.page === pagination.totalPages}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ListAuditPage
